import { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Action, UnregisterCallback } from 'history'

type PendingNavigation = {
  pathname: string
  search: string
  hash: string
  action: Action
}

export const useUnsavedChangesRouteGuard = (hasUnsaved: boolean) => {
  const history = useHistory()
  const unblockRef = useRef<UnregisterCallback | null>(null)
  const [pendingNavigation, setPendingNavigation] = useState<PendingNavigation | null>(null)

  useEffect(() => {
    if (!hasUnsaved) {
      unblockRef.current?.()
      unblockRef.current = null
      setPendingNavigation(null)
      return
    }

    // hasUnsaved が false → true と変化したときに二重登録しないためのガード
    if (unblockRef.current) return

    // ブラウザバック時のみ、history.blockで遷移をブロックしているにも関わらず、
    // 遷移はしないが、URL が遷移先に変更される問題が頻繁に起こる。
    // 既知の issue で未解決
    // https://github.com/remix-run/react-router/issues/5405
    // 解決するには react-routerのバージョンアップが必要だが、破壊的変更が入るため、許容する
    unblockRef.current = history.block((nextLocation, nextAction) => {
      setPendingNavigation({
        pathname: nextLocation.pathname,
        search: nextLocation.search,
        hash: nextLocation.hash,
        action: nextAction,
      })
      return false
    })

    // history.block は明示的に解除しないと残るため、不要時と unmount 時の両方で解放する
    return () => {
      unblockRef.current?.()
      unblockRef.current = null
    }
  }, [hasUnsaved, history])

  const confirmNavigation = useCallback(() => {
    if (!pendingNavigation) return
    const { pathname, search, hash, action } = pendingNavigation
    const nextLocation = { pathname, search, hash }

    unblockRef.current?.()
    unblockRef.current = null
    setPendingNavigation(null)

    if (action === 'REPLACE') {
      history.replace(nextLocation)
    } else {
      history.push(nextLocation)
    }
  }, [history, pendingNavigation])

  const cancelNavigation = useCallback(() => {
    setPendingNavigation(null)
  }, [])

  return {
    pendingNavigation,
    confirmNavigation,
    cancelNavigation,
  }
}
